import {Component, Input, OnInit} from '@angular/core';
import {IconColourEnum} from '../../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {ColDef} from 'ag-grid-community';
import {
  countAdvancedFilterConditions,
  countTopLevelFilterGroups,
  IAdvancedFilterGroup,
  IFilter,
  IFilterGroup,
} from '../../../../../features/settings-new/models';
import {
  ADVANCED_FILTER_GRID_GENERAL,
  ADVANCED_FILTER_GRID_STOCK_TAKE,
  AdvancedFilterGridCategory,
  isStockTakeAdvancedFilterGrid,
  matchesAdvancedFilterGridCategory,
} from '../../../../../features/settings-new/models/general/advanced-filter-grid.constants';
import {Store} from '@ngrx/store';
import {
  applyAdvancedFilterGroupFilterToGrid,
  cancelEditingOfAdvancedFilterGroup,
  cloneSelectedAdvancedFilterGroup,
  createAdvancedFilterGroup,
  deleteSelectedAdvancedFilterGroup,
  discardUnsavedAdvancedFilterGroup,
  removeAdvancedFilterGroupFilterFromGrid,
  saveAdvancedFilterGroup,
  saveSelectedAdvancedFilterGroup,
  setAdvancedFilterGroupGrid,
  setSelectedAdvancedFilterGroup,
} from '../../../../../features/core/store-shared-filter/shared-filter.actions';
import {getDeepCopyOfObject} from '../../../../shared-utils/object/object.utils';
import {ISharedModalBasic, ISharedModalBasicResponse} from '../../../../shared-models/modals/shared-modal-basic';
import {ISharedModalFiltersAdvanced} from '../../../../shared-models/modals/shared-modal-filters-advanced';
import {firstValueFrom, Observable} from 'rxjs';
import {
  selectSelectedAdvancedFilterGroupFilterGroups,
} from '../../../../../features/core/store-shared-filter/shared-filter.selectors';
import {DEFAULT_LOADER_INFINITE} from '../../../../shared-module-ui/models/animations';
import {SharedModalBasicComponent} from '../../shared-modal-basic/shared-modal-basic.component';
import {ISearchableFields} from '../../../../shared-models/type-sense/default-searchable-fields';
import {IconsEnum} from '../../../../../shared-modules/shared-icons/models/icons.enum';
import {IModalDismissResult, ModalControllerService} from '../../../../shared-services/overlay/modal-controller.service';
import {SharedModalUtils} from '../../../../shared-utils/shared-modal/shared-modal.utils';
import {selectSelectedStore} from '../../../../../features/user-access/user-stores-store/user-store.selectors';
import {StockTakeScheduleService} from '../../../../../features/stock-take/services/stock-take-schedule.service';
import {findSchedulesUsingAdvancedFilter} from '../../../../../features/stock-take/utils/stock-take-filter.utils';
import {IStockTakeScheduleEntry} from '../../../../../features/stock-take/models';

@Component({
  selector: 'app-shared-modal-filters-advanced',
  templateUrl: './shared-modal-filters-advanced.component.html',
  styleUrls: ['./shared-modal-filters-advanced.component.scss'],
  standalone: false,
})
export class SharedModalFiltersAdvancedComponent implements ISharedModalFiltersAdvanced, OnInit {


  @Input() buttonCloseCross: boolean;
  @Input() colDefs: ColDef[];
  @Input() colDefsIgnore: string[];
  @Input() modalTitle: string;
  @Input() isAdvancedFiltersLoading: boolean;
  @Input() lockCategoryTabs = false;
  appliedFilters: Record<string, ISearchableFields>;
  advancedFilterGroups: IAdvancedFilterGroup[] = [];
  appliedAdvancedFilterGroup: IAdvancedFilterGroup;
  colDefsNames: ColDef[] = [];
  selectedAdvancedFilterGroup: IAdvancedFilterGroup;
  selectedAdvancedFilterGroupOriginal: IAdvancedFilterGroup;
  private selectedAdvancedFilterGroupId: number | undefined;
  selectedGroupData$: Observable<IFilterGroup>;
  selectedGrid: string = ADVANCED_FILTER_GRID_GENERAL;
  filterSearchTerm: string = '';
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly animation = DEFAULT_LOADER_INFINITE;
  protected readonly advancedFilterGridGeneral = ADVANCED_FILTER_GRID_GENERAL;
  protected readonly advancedFilterGridStockTake = ADVANCED_FILTER_GRID_STOCK_TAKE;

  protected readonly icons = IconsEnum;

  constructor(
    private modalController: ModalControllerService,
    private readonly sharedModalUtils: SharedModalUtils,
    private readonly stockTakeScheduleService: StockTakeScheduleService,
    private readonly store: Store,
  ) {
  }

  get getColumnList(): ColDef[] {
    return this.colDefs.filter((col: ColDef) => !this.colDefsIgnore?.includes(col.field));
  }

  get getIsNewAdvancedFilterGroup(): boolean {
    const found = this.advancedFilterGroups.filter((filterGroup: IAdvancedFilterGroup) => !filterGroup.saved);
    return found.length > 0;
  }

  get filteredAdvancedFilterGroups(): IAdvancedFilterGroup[] {
    const normalizedSearch = this.filterSearchTerm?.trim().toLocaleLowerCase();
    if (!normalizedSearch) {
      return this.advancedFilterGroups;
    }

    return this.advancedFilterGroups.filter((advancedFilterGroup: IAdvancedFilterGroup): boolean => {
      const searchableText = this.buildSearchableText(advancedFilterGroup).toLocaleLowerCase();
      return searchableText.includes(normalizedSearch);
    });
  }

  @Input() set setAdvancedFilterGroups(filterGroups: IAdvancedFilterGroup[]) {
    this.advancedFilterGroups = [];
    if (filterGroups?.length > 0) {
      this.advancedFilterGroups = filterGroups.length > 0
        ? getDeepCopyOfObject(filterGroups).sort((a, b) => a.name.localeCompare(b.name))
        : [];
    }
  }

  @Input() set setAppliedFilter(appliedFilter: IAdvancedFilterGroup) {
    this.appliedAdvancedFilterGroup = undefined;
    if (appliedFilter) {
      this.appliedAdvancedFilterGroup = appliedFilter;
    }
  }

  @Input() set gridReference(grid: string) {
    this.selectedGrid = grid;
    this.store.dispatch(setAdvancedFilterGroupGrid({grid}));
  }

  @Input() set setSelectedAdvancedFilterGroup(advancedFilterGroup: IAdvancedFilterGroup) {
    this.selectedAdvancedFilterGroup = undefined;
    if (!advancedFilterGroup) {
      this.selectedAdvancedFilterGroupOriginal = undefined;
      this.selectedAdvancedFilterGroupId = undefined;
      return;
    }

    this.selectedAdvancedFilterGroup = getDeepCopyOfObject(advancedFilterGroup);

    const isNewSelection = advancedFilterGroup.id !== this.selectedAdvancedFilterGroupId;
    const isSavedBaseline = advancedFilterGroup.saved === true;
    if (isNewSelection && !isSavedBaseline) {
      this.selectedAdvancedFilterGroupOriginal = undefined;
      this.selectedAdvancedFilterGroupId = advancedFilterGroup.id;
      return;
    }
    if (isNewSelection || isSavedBaseline) {
      this.selectedAdvancedFilterGroupOriginal = getDeepCopyOfObject(advancedFilterGroup);
      this.selectedAdvancedFilterGroupOriginal.saved = true;
      this.selectedAdvancedFilterGroupId = advancedFilterGroup.id;
    }
  }

  @Input() set setSelectedFilters(filters: Record<string, ISearchableFields>) {
    if (filters && Object.keys(filters).length > 0) {
      this.appliedFilters = filters;
    }
  }

  ngOnInit(): void {
    this.colDefsNames = this.getColumnList;
    this.selectedGroupData$ = this.store.select(selectSelectedAdvancedFilterGroupFilterGroups);
  }

  getConditionCount(advancedFilterGroup: IAdvancedFilterGroup): number {
    return countAdvancedFilterConditions(advancedFilterGroup?.filterGroup);
  }

  getFilterGroupCount(advancedFilterGroup: IAdvancedFilterGroup): number {
    return countTopLevelFilterGroups(advancedFilterGroup?.filterGroup);
  }

  async onApplyFilterClick(): Promise<void> {
    if (!this.selectedAdvancedFilterGroup?.saved) {
      const result = await this.sharedModalUtils.createModal({
        modalTitle: 'Unsaved Changes',
        modalTitleIcon: IconsEnum.infoSlabCircleOutline,
        modalTitleIconColor: this.iconColourEnum.yellow,
        contextDescription: [
          'You have unsaved filter changes.',
          'Save your changes before applying to the grid?',
        ],
        buttonAcceptText: 'Save & Apply',
        buttonCloseText: 'Cancel',
        buttonAccept: true,
        buttonClose: true,
        buttonCloseCross: false,
        cssClass: ['shared-basic-modal-css-smaller'],
      });
      if (result.data?.buttonPressed !== 'button-accept') {
        return;
      }
      this.store.dispatch(saveSelectedAdvancedFilterGroup({advancedFilterGroup: this.selectedAdvancedFilterGroup}));
    }
    this.store.dispatch(applyAdvancedFilterGroupFilterToGrid({}));
  }

  onRemoveFilterClick(): void {
    this.store.dispatch(removeAdvancedFilterGroupFilterFromGrid());
  }

  async onButtonPress(buttonPressed: 'button-accept' | 'button-close'): Promise<void> {
    if (buttonPressed === 'button-close' && !await this.confirmDiscardChangesOnClose()) {
      return;
    }

    await this.modalController.dismiss(
      {buttonPressed} as ISharedModalBasicResponse,
      buttonPressed === 'button-accept' ? 'modal-accept' : 'modal-close',
    );
  }

  onCloneItemClick(): void {
    this.store.dispatch(cloneSelectedAdvancedFilterGroup());
  }

  onNameChange(): void {
    if (this.selectedAdvancedFilterGroup.name !== this.selectedAdvancedFilterGroupOriginal.name) {
      this.store.dispatch(saveAdvancedFilterGroup({advancedFilterGroup: this.selectedAdvancedFilterGroup}));
    }
  }

  onNewItemClick(): void {
    this.store.dispatch(createAdvancedFilterGroup({grid: this.selectedGrid}));
  }

  /**
   * Switch between General and Stock Take filter lists in the modal.
   */
  onCategoryTabClick(grid: AdvancedFilterGridCategory): void {
    if (this.lockCategoryTabs || this.selectedGrid === grid || this.getIsNewAdvancedFilterGroup) {
      return;
    }
    this.selectedGrid = grid;
    this.filterSearchTerm = '';
    this.selectedAdvancedFilterGroup = undefined;
    this.selectedAdvancedFilterGroupOriginal = undefined;
    this.selectedAdvancedFilterGroupId = undefined;
    this.store.dispatch(setAdvancedFilterGroupGrid({grid}));
    const applied = this.appliedAdvancedFilterGroup;
    const selectApplied = !!applied && matchesAdvancedFilterGridCategory(applied.grid, grid);
    this.store.dispatch(setSelectedAdvancedFilterGroup({
      advancedFilterGroup: selectApplied ? applied : null,
    }));
  }

  onSaveItemClick(): void {
    const shouldReapply =
      !this.lockCategoryTabs
      && this.selectedAdvancedFilterGroup?.id === this.appliedAdvancedFilterGroup?.id;
    this.store.dispatch(saveSelectedAdvancedFilterGroup({advancedFilterGroup: this.selectedAdvancedFilterGroup}));
    if (shouldReapply) {
      this.store.dispatch(applyAdvancedFilterGroupFilterToGrid({}));
    }
  }

  onCancelItemClick(): void {
    if (!this.selectedAdvancedFilterGroupOriginal) {
      this.store.dispatch(discardUnsavedAdvancedFilterGroup());
      return;
    }
    this.store.dispatch(cancelEditingOfAdvancedFilterGroup({
      originalAdvancedFilterGroup: getDeepCopyOfObject(this.selectedAdvancedFilterGroupOriginal),
    }));
  }

  onSelectedNameClick(id: number): void {
    const advancedFilterGroup = this.advancedFilterGroups.find((group: IAdvancedFilterGroup): boolean => group.id === id);
    this.store.dispatch(setSelectedAdvancedFilterGroup({advancedFilterGroup}));
    this.selectedAdvancedFilterGroup = undefined;
  }

  async onDeleteAdvancedFilterGroupClick(): Promise<void> {
    if (await this.blockDeleteIfUsedByStockTakeSchedule()) {
      return;
    }
    const componentProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Confirm Delete',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: `Cancel`,
      modalTitle: `Confirm Delete of Advanced Filter Group`,
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: this.iconColourEnum.yellow,
      contextHeading: 'NOTE: You are about to delete an entire advanced filter group.',
      contextDescription: [
        'Please make sure you want to perform the following delete.',
        ` - ${this.selectedAdvancedFilterGroup.name}`,
        'This action is irreversible.',
        `If this is you are certain please press 'Confirm Delete'.`,
        `If you would like to cancel press 'Cancel'.`,
      ],
    };
    const modalController = await this.modalController.create({
      component: SharedModalBasicComponent, componentProps, cssClass: ['shared-basic-modal-css'],
    });
    await modalController.present();
    const modalResponse: IModalDismissResult<ISharedModalBasicResponse> = await modalController.onDidDismiss();
    if (modalResponse?.data?.buttonPressed === 'button-accept') {
      this.onDeleteItemClick();
    }
  }

  private onDeleteItemClick(): void {
    this.store.dispatch(deleteSelectedAdvancedFilterGroup());
  }

  private async confirmDiscardChangesOnClose(): Promise<boolean> {
    if (this.selectedAdvancedFilterGroup?.saved !== false) {
      return true;
    }

    const modalResult = await this.sharedModalUtils.createModal({
      modalTitle: 'Discard Unsaved Changes?',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: this.iconColourEnum.yellow,
      contextDescription: [
        `"${this.selectedAdvancedFilterGroup.name}" has unsaved changes.`,
        'Close and discard these changes?',
      ],
      buttonAcceptText: 'Discard & Close',
      buttonCloseText: 'Keep Editing',
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-smaller'],
    });

    if (modalResult.data?.buttonPressed !== 'button-accept') {
      return false;
    }

    this.onCancelItemClick();
    return true;
  }

  /**
   * Blocks delete when a Stock Take filter is still referenced by one or more schedules.
   */
  private async blockDeleteIfUsedByStockTakeSchedule(): Promise<boolean> {
    const selected = this.selectedAdvancedFilterGroup;
    if (!selected || !isStockTakeAdvancedFilterGrid(selected.grid)) {
      return false;
    }

    const store = await firstValueFrom(this.store.select(selectSelectedStore));
    if (!store?.storeId) {
      return false;
    }

    const schedules = await firstValueFrom(
      this.stockTakeScheduleService.getScheduleEntries(store.storeId),
    );
    const inUse = findSchedulesUsingAdvancedFilter(selected.id, schedules);
    if (inUse.length === 0) {
      return false;
    }

    const scheduleNames = inUse.map(
      (schedule: IStockTakeScheduleEntry): string => ` - ${schedule.name}`,
    );
    await this.sharedModalUtils.createModal({
      modalTitle: 'Cannot Delete Filter',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.red,
      contextDescription: [
        `"${selected.name}" is used by ${inUse.length} stock take schedule${inUse.length === 1 ? '' : 's'}:`,
        ...scheduleNames,
        'Remove or change the filter on those schedules before deleting.',
      ],
      buttonAcceptText: '',
      buttonCloseText: 'OK',
      buttonAccept: false,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-small'],
    });
    return true;
  }

  private buildSearchableText(advancedFilterGroup: IAdvancedFilterGroup): string {
    const name = advancedFilterGroup?.name ?? '';
    const filterText = this.getFilterGroupSearchText(advancedFilterGroup?.filterGroup);
    return `${name} ${filterText}`.trim();
  }

  private getFilterGroupSearchText(filterGroup: IFilterGroup | undefined): string {
    if (!filterGroup?.filtersAndGroups?.length) {
      return '';
    }

    return filterGroup.filtersAndGroups
      .map((item: IFilterGroup | IFilter): string => {
        if ('filtersAndGroups' in item) {
          return this.getFilterGroupSearchText(item);
        }
        return `${item.column ?? ''} ${item.filterType ?? ''} ${item.value ?? ''}`;
      })
      .join(' ');
  }

}
